import React from 'react';
import { Platform, PressableProps } from 'react-native';
import { ExecutionContext } from 'styled-components/native/dist/types';
import styled from 'styled-components/native';
import { AppPressable } from '@/components/base/app.pressable';
import { AppTextView } from './app.text.view';
import { AppSpinner } from './app.spinner';
import { generateShadow } from '@/components/base/app.card.view';
import { theme } from '@/assets/theme';

interface ButtonProps extends PressableProps {
  title: string;
  variant?: 'primary' | 'blue' | 'red' | 'green' | 'grey' | 'orange';
  /**
   * outlined - button with an outline without background, typically used for important, but not primary action
   * contained - button with a background color, used for important action, have the most visual impact and high emphasis
   */
  mode?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
  fullWidth?: boolean;
  fontSize?: 'primary' | 'small' | 'big';
  rounded?: boolean;
  startChild?: any;
  endChild?: any;
  loading?: boolean;
  shadow?: 'green';
}

function BaseAppButton({
  style,
  onPress,
  variant = 'primary',
  mode = 'contained',
  fullWidth = true,
  rounded = false,
  fontSize = 'primary',
  shadow,
  loading,
  ...rest
}: ButtonProps) {
  const props = {
    ...rest,
    fullWidth,
    rounded,
    fontSize,
    mode,
    buttonVariant: variant,
  };

  return (
    <ContainerPressable
      {...props}
      onPress={rest.disabled || loading ? undefined : onPress}
      style={(pressedState: any) => {
        // styled-components passes an empty object if not pressed,
        // but Pressable passes { pressed: boolean }
        const { pressed } = pressedState || {};
        const isInteractive = !rest.disabled && !loading;
        let shadowStyle;
        if (shadow === 'green') {
          shadowStyle = generateShadow({ shadowColor: theme.colors.jadeDark, blurRadius: 12, shadowOpacity: 0.15, shadowOffsetX: 0, shadowOffsetY: 6 });
        }

        return [typeof style === 'function' ? style(pressedState) : style, { opacity: pressed && isInteractive ? 0.6 : 1 }, shadowStyle];
      }}
    >
      {rest.startChild && <ChildWrapper startChild={rest.startChild}>{rest.startChild}</ChildWrapper>}
      {loading ? <LoadingView {...props} /> : <ButtonText text={rest.title} {...props} buttonVariant={variant} />}
      {rest.endChild && <ChildWrapper {...props}>{rest.endChild}</ChildWrapper>}
    </ContainerPressable>
  );
}

type StyledProps = ExecutionContext & Omit<ButtonProps, 'variant'> & { buttonVariant: ButtonProps['variant'] };

function getBackgroundColor(props: StyledProps) {
  if (props.mode === 'outlined' || props.mode === 'text') {
    return 'transparent';
  }
  if (props.disabled) {
    return props.theme.colors.button.disabled.background;
  }
  return props.theme.colors.button[props.buttonVariant as any].background;
}

function getForegroundColor(props: StyledProps) {
  if (props.disabled) {
    return props.theme.colors.button.disabled.foreground;
  }
  if (props.mode === 'outlined' || props.mode === 'text') {
    return props.theme.colors.button[props.buttonVariant as any].background;
  }
  return props.theme.colors.button[props.buttonVariant as any].foreground;
}

function getBorderColor(props: StyledProps) {
  if (props.mode === 'text') {
    return 'transparent';
  }
  return props.theme.colors.button[props.buttonVariant as any].background;
}

// ${(props) => (props.fullWidth ? `width: ${Platform.OS === 'web' ? '100%' : 'auto'};` : 'align-self: baseline;')}
const ContainerPressable = styled(AppPressable)<StyledProps>`
  border-radius: ${(props) => (props.rounded ? 1000 : 10)}px;
  border-color: ${getBorderColor};
  border-width: ${(props) => (props.mode === 'outlined' ? 1 : 0)}px;
  ${(props) => props.fullWidth && `width: ${Platform.OS === 'web' ? '100%' : 'auto'};`}
  justify-content: center;
  align-items: center;
  flex-direction: row;
  background-color: ${getBackgroundColor};
  min-height: 45px;
  outline-style: none;
`;

const ButtonText = styled(AppTextView)<StyledProps>`
  margin-top: auto;
  margin-bottom: auto;
  color: ${getForegroundColor};
  font-size: ${(props) => props.theme.fontSizes.button[props.fontSize as any]}px;
  text-decoration: none;
  text-decoration-color: ${(props) =>
    props.disabled ? props.theme.colors.button.disabled.foreground : props.theme.colors.button[props.buttonVariant as any].foreground};
  font-weight: ${(props) => (props.fontSize === 'small' ? 400 : props.fontSize === 'primary' ? 500 : 600)};
  margin-right: ${(props) => (props.startChild ? '16px' : 'auto')};
  margin-left: ${(props) => (props.endChild ? '16px' : 'auto')};
  text-align: center;
`;

const ChildWrapper = styled.View<{ startChild?: any; endChild?: any }>`
  margin-right: ${(props) => (props.startChild ? 8 : 0)}px;
  margin-left: ${(props) => (props.endChild ? 8 : 0)}px;
`;

const LoadingView = styled(AppSpinner)<Omit<StyledProps, 'buttonVariant'>>`
  margin-top: auto;
  margin-bottom: auto;
  margin-right: ${(props) => (props.startChild ? 16 : 0)}px;
  margin-left: ${(props) => (props.endChild ? 16 : 0)}px;
  width: 45px;
  height: 45px;
`;

export const AppButton = React.memo(BaseAppButton);
